import React, { useEffect, useMemo,useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import {
  createAreaAction,
  clearCreateAreaAction,
  getAreaByIdAction,
  clearGetAreaByIdAction,
  updateAreaAction,
  clearUpdateAreaAction,
  getAllUserAction,
  getAllZonesAction,
  clearGetAllZonesAction,
  getAllRegionsAction,
  clearGetAllRegionsAction,
  getAllAreasAction,

} from "../../../store/actions";

const AddArea = ({ open, onClose, isEdit = false, areaId = "" }) => {
  const dispatch = useDispatch();
  const [duplicateError, setDuplicateError] = useState("");

  // Lists
  const { successData: usersData, loading: usersLoading } = useSelector((s) => s.getAllUserReducer || {});
  const { successData: zonesData, loading: zonesLoading } = useSelector((s) => s.getAllZonesReducer || {});
  const { successData: regionsData, loading: regionsLoading } = useSelector((s) => s.getAllRegionsReducer || {});
  const { successData: areasData, loading: areasLoading } = useSelector((s) => s.getAllAreasReducer || {});
  // Create/Update/ById
  const { successData: createSuccess, loading: createLoading, error: createError, errorInfo: createErrorInfo } = useSelector((s) => s.createAreaReducer || {});
  const { successData: areaByIdData, loading: areaByIdLoading, error: areaByIdError } = useSelector((s) => s.getAreaByIdReducer || {});
  const { successData: updateSuccess, loading: updateLoading, error: updateError, errorInfo: updateErrorInfo } = useSelector((s) => s.updateAreaReducer || {});

  const users = useMemo(() => {
    if (Array.isArray(usersData)) return usersData;
    if (usersData?.data?.users && Array.isArray(usersData.data.users)) return usersData.data.users;
    return [];
  }, [usersData]);

  const zones = useMemo(() => {
    if (Array.isArray(zonesData)) return zonesData;
    if (zonesData?.data?.zones && Array.isArray(zonesData.data.zones)) return zonesData.data.zones;
    return [];
  }, [zonesData]);

  const regions = useMemo(() => {
    if (Array.isArray(regionsData)) return regionsData;
    if (regionsData?.data?.regions && Array.isArray(regionsData.data.regions)) return regionsData.data.regions;
    return [];
  }, [regionsData]);

  const areas = useMemo(() => {
  if (Array.isArray(areasData)) return areasData;
  if (areasData?.data?.areas && Array.isArray(areasData.data.areas)) return areasData.data.areas;
  return [];
}, [areasData]);
  const amUsers = useMemo(() => {
    const list = Array.isArray(users) ? users : [];
    return list.filter((u) => {
      const role = (u.role || u.roleType || u.userRole || u.role_name || "").toString().toUpperCase();
      return role === "AM";
    });
  }, [users]);

  const areaSchema = Yup.object({
    area_name: Yup.string().trim().required("Area name is required"),
    region_id: Yup.number().typeError("Region ID must be a number").required("Region ID is required"),
    zone_id: Yup.number().typeError("Zone ID must be a number").required("Zone ID is required"),
  });

  const normalizeAreaName = (name) =>
  (name || "").replace(/\s+/g, "").toLowerCase();

  const formik = useFormik({
    initialValues: {
      area_name: "",
      region_id: "",
      zone_id: "",
      incharge_user_id: "",
    },
    validationSchema: areaSchema,
    enableReinitialize: false,
  onSubmit: (values) => {
  const newNameNorm = normalizeAreaName(values.area_name);

  const duplicate = areas.some((a) => {
    const aName = a?.areaName || a?.area_name || a?.name || "";
    const aRegionId = a?.regionId || a?.region_id || a?.regionID;
    const aZoneId = a?.zoneId || a?.zone_id || a?.zoneID;
    const aId = a?.areaId || a?.id || a?.area_id;

    if (isEdit && String(aId) === String(areaId)) return false;

    return (
      String(aRegionId) === String(values.region_id) &&
      String(aZoneId) === String(values.zone_id) &&
      normalizeAreaName(aName) === newNameNorm
    );
  });

  if (duplicate) {
    setDuplicateError("Area with this name already exists in this region and zone!");
    return;
  }

  setDuplicateError("");

  const payload = {
    area_name: values.area_name,
    region_id: Number(values.region_id),
    zone_id: Number(values.zone_id),
    incharge_user_id: values.incharge_user_id ? values.incharge_user_id : null,
  };

  if (isEdit && areaId) {
    dispatch(updateAreaAction({ ...payload, endPoint: `area/${areaId}` }));
  } else {
    dispatch(createAreaAction(payload));
  }
},
  });

  // Fetch lists and prefill when dialog opens
  useEffect(() => {
    if (!open) return;
    dispatch(getAllUserAction());
    dispatch(clearGetAllZonesAction());
    dispatch(getAllZonesAction());
    dispatch(clearGetAllRegionsAction());
    dispatch(getAllRegionsAction());
     dispatch(getAllAreasAction());
    if (isEdit && areaId) {
      dispatch(getAreaByIdAction({ endPoint: `area/${areaId}` }));
    } else {
      dispatch(clearGetAreaByIdAction());
      formik.resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, areaId]);

  // Prefill on get-by-id
  useEffect(() => {
    if (isEdit && areaByIdData && !areaByIdLoading && !areaByIdError) {
      const a = areaByIdData?.data?.area || areaByIdData?.data || areaByIdData;
      const prefill = {
        area_name: a?.area_name || a?.areaName || a?.name || "",
        region_id: a?.region_id ?? a?.regionId ?? a?.regionID ?? "",
        zone_id: a?.zone_id ?? a?.zoneId ?? a?.zoneID ?? "",
        incharge_user_id: a?.incharge_user_id || a?.inchargeUserId || a?.userId || "",
      };
      formik.setValues(prefill);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, areaByIdData, areaByIdLoading, areaByIdError]);

  useEffect(() => {
  if (!formik.values.area_name || !formik.values.region_id || !formik.values.zone_id) {
    setDuplicateError("");
    return;
  }

  const newNameNorm = normalizeAreaName(formik.values.area_name);

  const duplicate = areas.some((a) => {
    const aName = a?.areaName || a?.area_name || a?.name || "";
    const aRegionId = a?.regionId || a?.region_id || a?.regionID;
    const aZoneId = a?.zoneId || a?.zone_id || a?.zoneID;
    const aId = a?.areaId || a?.id || a?.area_id;

    if (isEdit && String(aId) === String(areaId)) return false;

    return (
      String(aRegionId) === String(formik.values.region_id) &&
      String(aZoneId) === String(formik.values.zone_id) &&
      normalizeAreaName(aName) === newNameNorm
    );
  });

  setDuplicateError(
    duplicate ? "Area with this name already exists in this region and zone!" : ""
  );
}, [formik.values.area_name, formik.values.region_id, formik.values.zone_id, areas, isEdit, areaId]);

  // Handle create/update results
  useEffect(() => {
    if (createSuccess) {
      toast.success("Area created successfully!");
      formik.resetForm();
      onClose?.();
      dispatch(clearCreateAreaAction());
      dispatch(getAllAreasAction());
    }
    if (createError) {
      toast.error(createErrorInfo || "Failed to create area");
      dispatch(clearCreateAreaAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createSuccess, createError, createErrorInfo]);

  useEffect(() => {
    if (updateSuccess) {
      toast.success("Area updated successfully!");
      formik.resetForm();
      onClose?.();
      dispatch(clearUpdateAreaAction());
      dispatch(clearGetAreaByIdAction());
      dispatch(getAllAreasAction());
    }
    if (updateError) {
      toast.error(updateErrorInfo || "Failed to update area");
      dispatch(clearUpdateAreaAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateSuccess, updateError, updateErrorInfo]);

  const handleCancel = () => {
    onClose?.();
    formik.resetForm();
    dispatch(clearGetAreaByIdAction());
  };

  const selectedZoneId = formik.values.zone_id;
  const filteredRegions = useMemo(() => {
    return regions.filter((r) => String(r.zone_id ?? r.zoneId ?? r.zoneID ?? "") === String(selectedZoneId ?? ""));
  }, [regions, selectedZoneId]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: 4, p: { xs: 2, sm: 4 } },
        },
      }}
    >
      <IconButton onClick={handleCancel} sx={{ position: "absolute", top: 16, right: 16 }}>
        <CloseIcon />
      </IconButton>

      <DialogTitle sx={{ p: 0, mb: 1 }}>
        <Typography fontSize={28} fontWeight={700}>Area Information</Typography>
        <Typography fontSize={14} color="text.secondary">{isEdit ? "Update Area" : "Create new Area"}</Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0, mt: 3 }}>
        <form onSubmit={formik.handleSubmit}>
          {/* Zone (Autocomplete) */}
          <Box mb={2}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Zone</Typography>
            <Autocomplete
              options={zones.map((z) => ({
                id: z.zoneId || z.id || z.zone_id || z.zoneID,
                name: z.zone_name || z.zoneName || z.name || String(z.zoneId || z.id || z.zone_id || z.zoneID || ""),
              })).filter((o) => o.id)}
              value={(function() {
                const currentId = formik.values.zone_id;
                if (!currentId) return null;
                const found = zones.find((z) => String(z.zoneId || z.id || z.zone_id || z.zoneID) === String(currentId));
                return found ? { id: found.zoneId || found.id || found.zone_id || found.zoneID, name: found.zone_name || found.zoneName || found.name || String(found.zoneId || found.id || found.zone_id || found.zoneID || "") } : null;
              })()}
              onChange={(_, newValue) => formik.setFieldValue("zone_id", newValue ? Number(newValue.id) : "")}
              loading={zonesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={zonesLoading ? "Loading zones..." : "Select a zone"}
                  fullWidth
                  size="small"
                  error={formik.touched.zone_id && Boolean(formik.errors.zone_id)}
                  helperText={formik.touched.zone_id && formik.errors.zone_id}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              )}
              getOptionLabel={(option) => option?.name ?? ""}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            />
          </Box>

          {/* Region (Autocomplete) */}
          <Box mb={2}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Region</Typography>
            <Autocomplete
              disabled={!selectedZoneId}
              options={filteredRegions.map((r) => ({
                id: r.regionId || r.id || r.region_id || r.regionID,
                name: r.region_name || r.regionName || r.name || String(r.regionId || r.id || r.region_id || r.regionID || ""),
              })).filter((o) => o.id)}
              value={(function() {
                const currentId = formik.values.region_id;
                if (!currentId) return null;
                const found = filteredRegions.find((r) => String(r.regionId || r.id || r.region_id || r.regionID) === String(currentId));
                return found ? { id: found.regionId || found.id || found.region_id || found.regionID, name: found.region_name || found.regionName || found.name || String(found.regionId || found.id || found.region_id || found.regionID || "") } : null;
              })()}
              onChange={(_, newValue) => formik.setFieldValue("region_id", newValue ? Number(newValue.id) : "")}
              loading={regionsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={!selectedZoneId ? "Select a zone first" : regionsLoading ? "Loading regions..." : (filteredRegions.length ? "Select a region" : "No regions for this zone")}
                  fullWidth
                  size="small"
                  error={formik.touched.region_id && Boolean(formik.errors.region_id)}
                  helperText={formik.touched.region_id && formik.errors.region_id}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              )}
              getOptionLabel={(option) => option?.name ?? ""}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
            />
          </Box>

          {/* Area Name */}
          <Box mb={2}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Area Name</Typography>
            <TextField
              name="area_name"
              value={formik.values.area_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              fullWidth
              size="small"
              placeholder="Enter Area"
           error={!!duplicateError || (formik.touched.area_name && Boolean(formik.errors.area_name))}
           helperText={duplicateError || (formik.touched.area_name && formik.errors.area_name)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
            />
          </Box>

          {/* Incharge User */}
          <Box mb={4}>
            <Typography fontSize={14} fontWeight={600} mb={1}>Incharge User</Typography>
            <Autocomplete
              options={amUsers}
              getOptionLabel={(option) => option.name || option.fullName || option.email || ""}
              isOptionEqualToValue={(option, value) => (option.userId || option.id) === (value.userId || value.id)}
              value={amUsers.find((u) => (u.userId || u.id) === formik.values.incharge_user_id) || null}
              onChange={(_, selected) => {
                formik.setFieldValue("incharge_user_id", selected ? (selected.userId || selected.id || "") : "");
              }}
              onBlur={() => formik.setFieldTouched("incharge_user_id", true)}
              disabled={usersLoading}
              loading={usersLoading}
              noOptionsText={amUsers.length === 0 ? "No AM users available" : "No match found"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={usersLoading ? "Loading users..." : "Search and select incharge"}
                  error={formik.touched.incharge_user_id && Boolean(formik.errors.incharge_user_id)}
                  helperText={formik.touched.incharge_user_id && formik.errors.incharge_user_id}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 3 } }}
                />
              )}
            />
          </Box>

          <Box display="flex" gap={2} flexDirection="row">
            <Button
              fullWidth
              variant="outlined"
              onClick={handleCancel}
              disabled={createLoading || updateLoading || areaByIdLoading}
              sx={{ borderRadius: 3, fontWeight: 600, p: "10px 0px", color: "#000", border: "1px solid #000", textTransform: "none" }}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={createLoading || updateLoading || areaByIdLoading || !!duplicateError}
              sx={{ borderRadius: 3, p: "10px 0px", fontWeight: 600, textTransform: "none", bgcolor: "#5B4CF5", boxShadow: "0px 8px 20px rgba(91,76,245,0.3)", "&:hover": { bgcolor: "#4a3de0" } }}
            >
              {createLoading || updateLoading ? (isEdit ? "Updating..." : "Submitting...") : (isEdit ? "Update Area" : "Submit Data")}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddArea;

AddArea.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  areaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};